import type { TFunction } from 'i18next'
import type { CSSProperties } from 'react'
import { useTranslation } from 'react-i18next'
import { twMerge } from 'tailwind-merge'
import type { VariantProps } from 'tailwind-variants'
import { tv } from 'tailwind-variants'
import { match, P } from 'ts-pattern'
import {
  useGenerationModelColorConfig,
  type ModelTypeTagColors,
} from 'src/components/GenerationModel/modelTypeDisplay'
import type {
  GenerationModelPreviewFragment,
  GenerationModelVersionPreviewFragment,
} from 'src/services/apollo/generated/graphql'
import { GenerationModelType } from 'src/services/apollo/generated/graphql'
import {
  isDitV1Model,
  isLora,
  isLoraUnion,
  isMMDIT26AModel,
  isModelPixaiInhouse,
  isMultiUnionLora,
  isSDModelUnion,
  isUserLoraUnion,
} from 'src/utils/model'
import { getModelAccessType } from '../../utils/generationModel/modelPermissions'
import Text from '../ui/Text'

const modelChipStyles = tv({
  slots: {
    base: 'flex items-center gap-2',
    label: 'font-medium text-xs',
  },
  variants: {
    type: {
      normal: {
        base: 'rounded-2xl px-3 py-1',
        label: 'md:text-base',
      },
      compact: {
        base: 'rounded w-fit h-auto px-1.5 py-0 mr-0',
        label: ':md:text-xs',
      },
    },
  },
  defaultVariants: {
    type: 'normal',
  },
})

/**
 * @see packages/pixai-server/src/service/generationTask.ts
 */
export enum TrainingTaskStatus {
  waiting = 'waiting',
  running = 'running',
  completed = 'completed',
  // Training task does not have "cancelled" status
  // cancelled = 'cancelled',
  failed = 'failed',
}

/**
 * @see packages/pixai-server/src/service/generationModelVersion.ts
 */
export enum GenerationModelVersionStatus {
  available = 'available',
  waiting = 'waiting',
  importing = 'importing',
  validating = 'validating',
  archived = 'archived',
  invalid = 'invalid',
}

type ModelTagType = 'pixai'

const TYPE_REPLACEMENT = {
  [GenerationModelType.UnknownModel]: GenerationModelType.SdV1Model,
  [GenerationModelType.UnknownLora]: GenerationModelType.Lora,
  [GenerationModelType.MultiLora]: GenerationModelType.Lora,
  [GenerationModelType.UserMultiLora]: GenerationModelType.Lora,
  [GenerationModelType.UserLora]: GenerationModelType.Lora,
  [GenerationModelType.UserSdxlLora]: GenerationModelType.Lora,
  [GenerationModelType.SdxlLora]: GenerationModelType.Lora,
  /** Preparation for Dit7B deprecation */
  [GenerationModelType.Dit7BLora]: GenerationModelType.Dit7Lora,
  [GenerationModelType.Dit7BModel]: GenerationModelType.Dit7Model,
  [GenerationModelType.Dit7BVae]: GenerationModelType.Dit7Vae,
} as const
const TYPE_REPLACEMENT_XL = {
  ...TYPE_REPLACEMENT,
  [GenerationModelType.SdxlModel]: GenerationModelType.SdV1Model,
  [GenerationModelType.SdxlVae]: GenerationModelType.Vae,
} as const
const typeReplacementMap = (replaceXL?: boolean) =>
  (replaceXL ? TYPE_REPLACEMENT_XL : TYPE_REPLACEMENT) as Record<
    GenerationModelType,
    GenerationModelType
  >

/**
 * Maps GenerationModelType to a dynamic config key for
 * modelTypeTagColors color lookup.
 * Note: inHouse only affects the label prefix (via i18n),
 * the color is always determined by the actual model type.
 */
function resolveModelTypeTagColorKey(type?: GenerationModelType): string {
  return (
    match(type)
      .with(GenerationModelType.MultiLora, () => 'MULTI_LORA')
      .with(GenerationModelType.UserMultiLora, () => 'USER_MULTI_LORA')
      // Consider removing UnknownLora. It's only being used in model submission form.
      .with(GenerationModelType.UnknownLora, () => 'MULTI_LORA')
      .with(GenerationModelType.SdV1Model, () => 'SD_V1')
      .with(GenerationModelType.SdxlModel, () => 'SDXL')
      .with(GenerationModelType.Sd3MediumModel, () => 'SD3')
      .when(isMMDIT26AModel, () => 'MMDIT26A')
      .when(isDitV1Model, () => 'DIT7')
      .with(GenerationModelType.Chat, () => 'CHAT')
      .with(GenerationModelType.VideoModel, () => 'VIDEO')
      .with(GenerationModelType.VideoModelLora, () => 'VIDEO_LORA')
      .otherwise(() => 'default')
  )
}

export const formatModelType = (
  type: GenerationModelType | undefined,
  opts: {
    /**
     * Some model types shouldn't be displayed as-is.
     * Replace them for simpler i18n messages.
     */
    replacement?: Record<GenerationModelType, string>
    tagType?: ModelTagType
  },
) => {
  const _type = (() => {
    let parsedType: string = type ?? GenerationModelType.UnknownModel
    if (opts?.replacement) {
      parsedType = (type ? opts.replacement[type] : undefined) ?? parsedType
    }
    /**
     * Prepend tag type to differentiate copywriting
     * for models with specific tags
     */
    if (opts?.tagType) {
      parsedType = `${opts.tagType}-${parsedType}`
    }
    return parsedType
  })()

  return _type.replaceAll('_', '').toLowerCase()
}

export const getModelTypeChipConfig = (
  t: TFunction,
  modelProps: {
    type: GenerationModelType
    curations?: GenerationModelPreviewFragment['curations']
  },
  colorConfig?: ModelTypeTagColors,
): {
  label: string
  bgStyle: CSSProperties
  bgClassName: string
} => {
  const { type } = modelProps
  const tagType = isModelPixaiInhouse(modelProps.curations)
    ? 'pixai'
    : undefined
  const formatTypeOpts = (...args: Parameters<typeof typeReplacementMap>) =>
    ({
      replacement: typeReplacementMap(...args),
      tagType,
    }) as Parameters<typeof formatModelType>[1]

  const modelTypeKey = formatModelType(type, formatTypeOpts())
  const label = t(`market:model.type.${modelTypeKey}`, {
    defaultValue: t('market:model.type.unknown'),
  })

  // Dynamic config color (only when exact key exists)
  const configKey = resolveModelTypeTagColorKey(type)
  const tagColor = colorConfig?.[configKey]

  const bgColor = match({ type, tagType, tagColor })
    .returnType<string>()
    .with({ tagType: 'pixai' }, () => {
      // Check for inhouse first, read from dyn config
      const inHouseColor = colorConfig?.['inHouse']
      if (inHouseColor) return inHouseColor.color

      // Fallback to hardcoded color
      return '#E4C1F9'
    })

    // Other dynamic config colors, return directly if valid
    .with({ tagColor: P.select({ color: P.string }) }, ({ color }) => {
      return color
    })

    // Start hardcoded colors
    // "PixAI Inhouse" and UserLoras share the same purple color
    .with({ type: isUserLoraUnion }, () => '#E4C1F9')
    .with({ type: isLoraUnion }, () => '#A5B4FC')
    .with({ type: GenerationModelType.VideoModel }, () => '#BEE3F8')
    .with({ type: GenerationModelType.SdV1Model }, () => '#FCF6BD')
    .with({ type: GenerationModelType.SdxlModel }, () => '#D0F4DD')
    .with({ type: GenerationModelType.Sd3MediumModel }, () => '#A9DDF9')
    .with({ type: GenerationModelType.Mmdit26AModel }, () => '#F9C1D9')
    .with(
      { type: isSDModelUnion },
      () => '#93C5FD', // bg-blue-300
    )
    .otherwise(
      () => '#D6D3D1', // bg-stone-300
    )

  return {
    label,
    bgStyle: {
      '--chip-bg-color': bgColor,
    },
    bgClassName: 'bg-[--chip-bg-color]',
  }
}

export default function ModelTypeChip(
  props: StyleProps & {
    model: Pick<GenerationModelPreviewFragment, 'type' | 'curations'>
  } & VariantProps<typeof modelChipStyles>,
) {
  const { t } = useTranslation(['market'])
  const colorConfig = useGenerationModelColorConfig()

  const { label, bgClassName, bgStyle } = getModelTypeChipConfig(
    t,
    {
      type: props.model.type,
      curations: props.model.curations,
    },
    colorConfig,
  )

  const styles = modelChipStyles({ type: props.type })

  return (
    <div
      className={styles.base({
        class: ['text-black', props.className, bgClassName],
      })}
      style={{
        ...props.style,
        ...bgStyle,
      }}
    >
      <p className={styles.label()}>{label}</p>
    </div>
  )
}

export const getModelPreviewEAChipConfig = (
  model: Pick<GenerationModelPreviewFragment, 'accessType'>,
) => {
  const accessType = getModelAccessType(model)
  return match(accessType)
    .with('early_access', () => ({
      label: <Text i18nKey="market:model.early-access.label" />,
      bgClassName: 'bg-purple-600',
    }))
    .with('preview', () => ({
      label: <Text i18nKey="market:model.preview-test.label" />,
      bgClassName: 'bg-purple-600',
    }))
    .otherwise(() => null)
}

/**
 * Display model's preview or early access status
 * Usually user-specific
 *
 * @param props
 * @returns
 */
export function ModelPreviewEAChip(
  props: StyleProps & {
    model: Pick<GenerationModelPreviewFragment, 'accessType'>
  } & VariantProps<typeof modelChipStyles>,
) {
  const config = getModelPreviewEAChipConfig(props.model)

  if (!config) return null

  const styles = modelChipStyles({ type: props.type })

  return (
    <div
      className={styles.base({
        class: ['text-white', props.className, config.bgClassName],
      })}
      style={props.style}
    >
      <p className={styles.label()}>{config.label}</p>
    </div>
  )
}

export function getModelLoraBaseModelChipsConfig(
  model: Pick<GenerationModelPreviewFragment, 'loraBaseModelTypes' | 'type'>,
  colorConfig?: ModelTypeTagColors,
) {
  const { loraBaseModelTypes, type } = model
  const types = isMultiUnionLora(type) ? loraBaseModelTypes : [type]
  return types?.flatMap((type: string) => {
    const config = getModelLoraBaseModelChipConfig(type, colorConfig)
    if (config) return [config]
    return []
  })
}

/** Resolves a short display label for a MultiLora's base model. */
function resolveLoraBaseModelLabel(type?: string): string | undefined {
  return match(type)
    .when(isMMDIT26AModel, () => 'DiT.2')
    .when(isDitV1Model, () => 'DiT.1')
    .with(
      P.union(
        GenerationModelType.SdxlModel,
        GenerationModelType.SdxlLora,
        GenerationModelType.UserSdxlLora,
      ),
      () => 'XL',
    )
    .with(
      P.union(
        GenerationModelType.SdV1Model,
        GenerationModelType.Lora,
        GenerationModelType.UserLora,
      ),
      () => 'SD',
    )
    .otherwise(() => undefined)
}

export function getModelLoraBaseModelChipConfig(
  type?: string,
  colorConfig?: ModelTypeTagColors,
):
  | {
      label: string
      bgStyle: CSSProperties
      bgClassName: string
    }
  | undefined {
  const label = resolveLoraBaseModelLabel(type)
  if (!label) return undefined

  // Dynamic config color (only when exact key exists)
  const configKey = resolveModelTypeTagColorKey(type as GenerationModelType)
  const tagColor = colorConfig?.[configKey]

  const bgColor = match({ type, tagColor })
    .returnType<string | undefined>()
    // Return dynamic color directly if valid
    .with({ tagColor: P.select({ color: P.string }) }, ({ color }) => color)
    // Start hardcoded colors
    .when(
      ({ type }) => isMMDIT26AModel(type),
      () => '#F9C1D9',
    )
    .when(
      ({ type }) => isDitV1Model(type),
      () => '#E4C1F9',
    )
    .with({ type: GenerationModelType.SdxlModel }, () => '#D0F4DD')
    .with({ type: GenerationModelType.SdV1Model }, () => '#FCF6BD')
    .otherwise(() => undefined)

  if (!bgColor) return undefined
  return {
    label,
    bgStyle: { '--chip-bg-color': bgColor },
    bgClassName: 'bg-[--chip-bg-color]',
  }
}

/**
 * Basically same as ModelChip, but for different base model types
 * Returns a group of chips
 * @param props
 * @returns
 */
export function ModelLoraBaseTypeChips(
  props: StyleProps & {
    model: Pick<GenerationModelPreviewFragment, 'type'>
    labelStyleProps?: StyleProps
  } & VariantProps<typeof modelChipStyles>,
) {
  const colorConfig = useGenerationModelColorConfig()

  if (!isLora(props.model.type)) return null

  const loraBaseModelConfig =
    getModelLoraBaseModelChipsConfig(props.model, colorConfig) ?? []

  const styles = modelChipStyles({ type: props.type })

  return (
    <div
      className={twMerge('inline-flex items-center', props.className)}
      style={props.style}
    >
      {loraBaseModelConfig.map(config => (
        <span
          className={styles.base({
            class: [
              'text-black first-of-type:rounded-l-2xl first-of-type:pl-3 last-of-type:rounded-r-2xl last-of-type:pr-3',
              props.labelStyleProps?.className,
              config.bgClassName,
            ],
          })}
          key={config.label}
          style={{
            ...props.labelStyleProps?.style,
            ...config.bgStyle,
          }}
        >
          <span className={styles.label()}>{config.label}</span>
        </span>
      ))}
    </div>
  )
}

/**
 * If version isn't provided, will treat as "no available versions"
 */
export function getModelVersionStatusChipConfig(
  t: TFunction,
  version?: Pick<GenerationModelVersionPreviewFragment, 'status'>,
  opts?: {
    // Available status is not shown by default
    showAvailable?: boolean
  },
) {
  if (!version)
    return {
      label: t('market:model.versions-empty'),
      bgClassName: 'bg-slate-300',
    }
  return (
    match(version.status)
      .with(GenerationModelVersionStatus.waiting, () => {
        const bgClassName = 'bg-yellow-500'
        const label = t('market:model.version-status.waiting')
        return { label, bgClassName }
      })
      .with(
        (GenerationModelVersionStatus.importing,
        GenerationModelVersionStatus.validating),
        () => {
          const bgClassName = 'bg-yellow-500'
          const label = t('market:model.version-status.running')
          return { label, bgClassName }
        },
      )
      .with(GenerationModelVersionStatus.archived, () => ({
        label: t('market:model.version-status.archived'),
        bgClassName: 'bg-slate-300',
      }))
      .with(GenerationModelVersionStatus.invalid, () => ({
        label: t('market:model.version-status.invalid'),
        bgClassName: 'bg-red-300',
      }))
      .with(GenerationModelVersionStatus.available, () =>
        // Return nothing unless explicitly needed to show
        opts?.showAvailable
          ? {
              label: t('market:model.version-status.available'),
              bgClassName: 'bg-green-300',
            }
          : undefined,
      )
      // For unknown status, don't render anything atm
      .otherwise(() => undefined)
  )
}

export function ModelVersionStatusChip(
  props: {
    modelVersion?: Pick<GenerationModelVersionPreviewFragment, 'status'>
    chipConfigOpts?: Parameters<typeof getModelVersionStatusChipConfig>[2]
  } & VariantProps<typeof modelChipStyles> &
    StyleProps,
) {
  const { t } = useTranslation('market')
  const config = getModelVersionStatusChipConfig(
    t,
    props.modelVersion,
    props.chipConfigOpts,
  )

  if (!config) return null

  const styles = modelChipStyles({ type: props.type })

  return (
    <div
      className={styles.base({
        class: ['text-white', props.className, config.bgClassName],
      })}
      style={props.style}
    >
      <p className={styles.label()}>{config.label}</p>
    </div>
  )
}
